import { useEffect, useMemo, useRef, useState } from 'react';
import { api, getToken } from '@/api/client';

// How many recent files the sidebar shows inline. The rest live behind the
// "Browse all files" modal — the sidebar is a peek, not the whole drawer.
const SIDEBAR_MAX = 6;

// The workspace tree served by GET /api/workspace-tree. The endpoint has existed
// (and been sandboxed per-user) all along; this panel is the UI it was built for.
interface FileNode { kind: 'file'; name: string; path: string; size: number; mtimeMs: number }
interface DirNode { kind: 'dir'; name: string; path: string; children: TreeNode[] }
type TreeNode = FileNode | DirNode;
interface TreeResponse { root: string; rootAbs: string; tree: TreeNode[]; truncated: boolean }

/** Same-origin URL to /api/preview for a workspace-relative path. `download`
 *  flips the one flag that turns an inline render into a save. Mirrors the
 *  helpers in Messages.tsx so a file opens identically wherever it is surfaced. */
function previewHref(path: string, download: boolean): string {
  const t = getToken();
  const q = new URLSearchParams({ path });
  if (download) q.set('download', '1');
  if (t) q.set('token', t);
  return `/api/preview?${q.toString()}`;
}

/** Human file size — bytes are noise in a file list. */
function humanSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Types the pane can render (native, or server-converted Office → HTML/PDF).
 *  Kept in step with PreviewPane's list; anything else is download-only. */
function isInlineViewable(name: string): boolean {
  return /\.(html?|pdf|png|jpe?g|gif|webp|svg|txt|md|json|csv|docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/i.test(name.trim());
}

/** A short, colour-toned badge for a file's type — the little "kind" glyph a Mac
 *  file picker shows at the left of each row. `tone` maps to a CSS accent class so
 *  each family reads at a glance (PDF red, deck orange, sheet green, image violet). */
function fileBadge(name: string): { label: string; tone: string } {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (/^html?$/.test(ext)) return { label: 'HTML', tone: 'html' };
  if (ext === 'pdf') return { label: 'PDF', tone: 'pdf' };
  if (/^(pptx?|odp)$/.test(ext)) return { label: 'PPT', tone: 'deck' };
  if (/^(docx?|odt|rtf)$/.test(ext)) return { label: 'DOC', tone: 'doc' };
  if (/^(xlsx?|csv|ods)$/.test(ext)) return { label: ext === 'csv' ? 'CSV' : 'XLS', tone: 'sheet' };
  if (/^(png|jpe?g|gif|webp|svg)$/.test(ext)) return { label: 'IMG', tone: 'img' };
  if (/^(json|md|txt)$/.test(ext)) return { label: ext.toUpperCase(), tone: 'text' };
  return { label: (ext || 'FILE').slice(0, 4).toUpperCase(), tone: 'other' };
}

/** All files newest-first — the global order both the sidebar peek and the modal
 *  draw from. */
function newestFirst(files: FileNode[]): FileNode[] {
  return [...files].sort((a, b) => b.mtimeMs - a.mtimeMs);
}

/** A macOS-Finder-style document icon: a paper sheet with a folded corner and a
 *  colour-toned band carrying the file type. Reads as a real file at a glance —
 *  the "kind" glyph a Mac open-panel shows at the left of each row. Paper/edge
 *  colours come from theme vars so it tracks light/dark; the band is the family
 *  tone (set via `color` on the wrapper). */
function FileGlyph({ label, tone }: { label: string; tone: string }): JSX.Element {
  return (
    <span className={`files-modal__glyph files-modal__glyph--${tone}`} aria-hidden="true">
      <svg width="28" height="34" viewBox="0 0 28 34" fill="none">
        <path className="files-modal__glyph-page"
          d="M8 2 H16 L22 8 V30 Q22 32 20 32 H8 Q6 32 6 30 V4 Q6 2 8 2 Z" strokeWidth="1.2" />
        <path className="files-modal__glyph-fold" d="M16 2 V6 Q16 8 18 8 H22 Z" strokeWidth="1.2" strokeLinejoin="round" />
        <rect className="files-modal__glyph-band" x="6" y="20" width="16" height="8" rx="1.6" />
        <text className="files-modal__glyph-type" x="14" y="25.7" textAnchor="middle">{label}</text>
      </svg>
    </span>
  );
}

/** Compact "date modified" for the Finder column — a time for today/yesterday,
 *  a short date beyond that (with the year only when it isn't the current one). */
function shortModified(mtimeMs: number): string {
  const d = new Date(mtimeMs);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 86_400_000;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (mtimeMs >= startToday) return `Today ${time}`;
  if (mtimeMs >= startToday - DAY) return `Yesterday ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Flatten the nested tree into a flat file list — the panel groups by DAY, not
 *  by folder, so the folder nesting is collapsed away (the full path stays as the
 *  row's tooltip). */
function flattenFiles(nodes: TreeNode[]): FileNode[] {
  const out: FileNode[] = [];
  const walk = (ns: TreeNode[]): void => {
    for (const n of ns) {
      if (n.kind === 'file') out.push(n);
      else walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Which day-bucket a file falls in, by its mtime. Lower order sorts higher, so
 *  Today lands at the top. Buckets mirror the familiar chat-history grouping. */
function dayBucket(mtimeMs: number): { order: number; label: string } {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const DAY = 86_400_000;
  if (mtimeMs >= startToday) return { order: 0, label: 'Today' };
  if (mtimeMs >= startToday - DAY) return { order: 1, label: 'Yesterday' };
  if (mtimeMs >= startToday - 7 * DAY) return { order: 2, label: 'Previous 7 days' };
  if (mtimeMs >= startToday - 30 * DAY) return { order: 3, label: 'Previous 30 days' };
  return { order: 4, label: 'Older' };
}

/** Files grouped into day-buckets, newest bucket first and newest file first
 *  within each. */
function groupByDay(files: FileNode[]): Array<{ label: string; files: FileNode[] }> {
  const byOrder = new Map<number, { order: number; label: string; files: FileNode[] }>();
  for (const f of files) {
    const b = dayBucket(f.mtimeMs);
    let g = byOrder.get(b.order);
    if (!g) { g = { order: b.order, label: b.label, files: [] }; byOrder.set(b.order, g); }
    g.files.push(f);
  }
  return [...byOrder.values()]
    .sort((a, b) => a.order - b.order)
    .map((g) => ({ label: g.label, files: g.files.sort((a, b) => b.mtimeMs - a.mtimeMs) }));
}

function FileRow(
  { node, onOpen }: { node: FileNode; onOpen?: ((path: string, name: string) => void) | undefined },
): JSX.Element {
  return (
    <div className="files__row files__row--file" title={node.path}>
      {onOpen ? (
        // Preferred: open in the in-app preview pane (no new tab).
        <button type="button" className="files__name files__name--link files__name--btn"
                onClick={() => onOpen(node.path, node.name)}>
          {node.name}
        </button>
      ) : (
        // Fallback (pane not wired): open through the browser as before.
        <a className="files__name files__name--link" href={previewHref(node.path, !isInlineViewable(node.name))}
           target="_blank" rel="noopener noreferrer">
          {node.name}
        </a>
      )}
      <span className="files__size">{humanSize(node.size)}</span>
      <a
        className="files__dl"
        href={previewHref(node.path, true)}
        title={`Download ${node.name}`}
        download
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </div>
  );
}

/** One row of the Finder-style list: a document glyph + name (opens the preview
 *  pane), a Date-Modified column, a Size column, and a hover download. Laid out on
 *  the same grid as the column header so the columns line up down the list. */
function ModalRow(
  { node, onOpen }: { node: FileNode; onOpen?: ((path: string, name: string) => void) | undefined },
): JSX.Element {
  const badge = fileBadge(node.name);
  const open = (): void => onOpen?.(node.path, node.name);
  return (
    <div className="files-modal__row" title={node.path}>
      <button
        type="button"
        className="files-modal__open"
        onClick={onOpen ? open : undefined}
        {...(onOpen ? {} : { disabled: true })}
      >
        <FileGlyph label={badge.label} tone={badge.tone} />
        <span className="files-modal__name">{node.name}</span>
      </button>
      <span className="files-modal__modified">{shortModified(node.mtimeMs)}</span>
      <span className="files-modal__size">{humanSize(node.size)}</span>
      <a className="files-modal__dl" href={previewHref(node.path, true)} title={`Download ${node.name}`} download>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </div>
  );
}

/**
 * Full workspace browser — a Mac-file-picker-style modal opened from the sidebar's
 * "Browse all files". Shows every file grouped by day, with a live name filter and
 * type badges. Opening a file routes through the same preview pane as everywhere
 * else, then closes the modal so the pane isn't hidden behind it.
 *
 * Backdrop-mousedown and Escape both close; focus moves to the search box on open
 * and is restored on close — same interaction contract as the other app modals.
 */
function FilesModal(
  { files, onOpen, onClose }: {
    files: FileNode[];
    onOpen?: ((path: string, name: string) => void) | undefined;
    onClose: () => void;
  },
): JSX.Element {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Esc closes; move focus to search on open, restore it on close.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    window.addEventListener('keydown', onKey, true);
    const prev = document.activeElement as HTMLElement | null;
    searchRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? files.filter((f) => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q)) : files),
    [files, q],
  );
  const groups = groupByDay(filtered);

  // Open in the pane AND dismiss — otherwise the modal covers the pane it opened.
  const openAndClose = onOpen
    ? (path: string, name: string) => { onOpen(path, name); onClose(); }
    : undefined;

  return (
    <div className="settings-overlay files-modal-overlay" onMouseDown={onClose}>
      <div
        className="settings-modal files-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Workspace files"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="files-modal__head">
          <div className="files-modal__heading">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
            <h2 className="files-modal__title">Workspace files</h2>
            <span className="files-modal__count">{files.length}</span>
          </div>
          <button type="button" className="settings-modal__close" aria-label="Close" title="Close" onClick={onClose}>×</button>
        </header>

        <div className="files-modal__search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search files…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search files"
          />
          {query && (
            <button type="button" className="files-modal__clear" onClick={() => setQuery('')} aria-label="Clear search">×</button>
          )}
        </div>

        <div className="files-modal__cols" aria-hidden="true">
          <span className="files-modal__col-name">Name</span>
          <span>Date Modified</span>
          <span>Size</span>
          <span />
        </div>

        <div className="files-modal__body">
          {groups.length === 0 ? (
            <div className="files-modal__empty">No files match “{query}”.</div>
          ) : (
            groups.map((g) => (
              <div className="files-modal__group" key={g.label}>
                <div className="files-modal__group-label">{g.label}</div>
                {g.files.map((f) => <ModalRow key={f.path} node={f} onOpen={openAndClose} />)}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Files sidebar panel — the browser for everything the agent has created in this
 * user's workspace, across every chat. Until now files only appeared as chips
 * under the message that produced them, so closing the chat lost sight of them.
 *
 * Reads GET /api/workspace-tree (nested, sandboxed, depth/entry-capped on the
 * server) and opens each file through GET /api/preview — the same sandboxed
 * stream the in-chat download chips use, so nothing new is exposed.
 *
 * Fetches LAZILY on first open (like ExtensionsPanel): the tree walk is real
 * server I/O, so a panel the user rarely opens should not cost every page boot.
 */
export function FilesPanel(
  { expanded, onOpen, refreshKey }: {
    expanded: boolean;
    onOpen?: ((path: string, name: string) => void) | undefined;
    /** Bumped by the parent when a turn finishes, so a file the agent just
     *  created appears without the user hitting refresh. */
    refreshKey?: number | undefined;
  },
): JSX.Element {
  const [data, setData] = useState<TreeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  function load(): void {
    setError(null);
    setLoading(true);
    api.get<TreeResponse>('/api/workspace-tree')
      .then((r) => setData(r))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  // Load whenever the panel opens OR the parent signals a turn finished
  // (refreshKey bump), so a file the agent just wrote shows up on its own — the
  // old behaviour only fetched once and needed a manual refresh. A tree read is
  // cheap; the effect only fires on open / after a turn, never per keystroke.
  useEffect(() => {
    if (expanded) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, refreshKey]);

  if (!expanded) return <></>;

  // Flatten once and drive everything off the file count — a workspace that has
  // only (empty) folders would otherwise read as non-empty yet render nothing.
  const files = data ? flattenFiles(data.tree) : [];
  const empty = data && files.length === 0;
  // The sidebar is a peek: the newest SIDEBAR_MAX files, still day-grouped. The
  // full set lives behind the modal.
  const recent = newestFirst(files).slice(0, SIDEBAR_MAX);
  const groups = groupByDay(recent);
  const overflow = files.length - recent.length;

  return (
    <div className="files-panel" role="region" aria-label="Workspace files">
      <div className="files-panel__head">
        <span className="files-panel__title">Workspace files</span>
        <button type="button" className="files-panel__refresh" onClick={load} title="Refresh" aria-label="Refresh">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M20 4v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {loading && !data && <div className="files-panel__msg">Loading…</div>}
      {error && <div className="files-panel__msg files-panel__msg--err">{error}</div>}
      {empty && <div className="files-panel__msg">No files yet. Files the assistant creates show up here.</div>}

      {data && files.length > 0 && (
        <div className="files-panel__tree">
          {groups.map((g) => (
            <div className="files-panel__group" key={g.label}>
              <div className="files-panel__group-label">{g.label}</div>
              {g.files.map((f) => <FileRow key={f.path} node={f} onOpen={onOpen} />)}
            </div>
          ))}
          {(overflow > 0 || data.truncated) && (
            <button type="button" className="files-panel__more" onClick={() => setShowAll(true)}>
              Browse all files{overflow > 0 ? ` (${files.length})` : ''}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      )}

      {showAll && (
        <FilesModal files={newestFirst(files)} onOpen={onOpen} onClose={() => setShowAll(false)} />
      )}
    </div>
  );
}
